import axios from 'axios';
import Utils from '../util';

// Synapse backend root (client builds /api/v1/... paths).
const BASE_URL = process.env.REACT_APP_BASE_URL;
// Central auth (SynapseAuth) root — the browser calls /v1/auth/* here directly.
const AUTH_BASE_URL = process.env.REACT_APP_AUTH_BASE_URL;

// Own instance rather than `axios.defaults.baseURL = BASE_URL`. Mutating the
// global default pointed every bare-axios caller in the dependency tree at the
// Synapse API root, and hung our interceptors — bearer token, refresh-on-401,
// hard logout — on requests this app never made. The auth client below already
// did this correctly.
const apiAxios = axios.create({ baseURL: BASE_URL });

// Dedicated instance for the central auth server (login/refresh/logout/password).
const authAxios = axios.create({ baseURL: AUTH_BASE_URL });

// ─── token helpers ─────────────────────────────────────────────────────────
const readToken = (key) => {
  const raw = localStorage.getItem(key);
  if (!raw || raw === 'undefined' || raw === 'null') return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const clearSession = () => {
  localStorage.removeItem('user');
  localStorage.removeItem('auth_token');
  localStorage.removeItem('refresh_token');
};

const hardLogout = () => {
  clearSession();
  window.location.replace('/');
};

// ─── refresh (single-flight, hits AUTH directly) ─────────────────────────────
// Uses bare fetch so it never re-enters an axios interceptor. Auth returns
// { access, refresh }; we persist them under the app's auth_token/refresh_token.
//
// Exported because the socket layer needs the SAME rotation: the websocket
// handshake carries the persisted access token, and when that token has expired
// the server refuses the connection. Sharing this promise means a 401 on HTTP
// and a refused handshake collapse into one refresh rather than two.
let refreshPromise = null;

export const refreshAccessToken = () => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const refresh = readToken('refresh_token');
      if (!refresh || !AUTH_BASE_URL) return null;
      try {
        const res = await fetch(`${AUTH_BASE_URL}/v1/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh, app: 'synapse' }),
        });
        if (!res.ok) return null;
        const json = await res.json().catch(() => ({}));
        const data = json?.data ?? {};
        if (!data.access) return null;
        localStorage.setItem('auth_token', JSON.stringify(data.access));
        if (data.refresh) localStorage.setItem('refresh_token', JSON.stringify(data.refresh));
        return data.access;
      } catch {
        return null;
      }
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

// ─── shared interceptors ─────────────────────────────────────────────────────
const attachBearer = (config) => {
  const token = readToken('auth_token');
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
};

// Central EdDSA access tokens are short-lived: a 401 means "rotate", not "log
// out". Refresh once (shared across concurrent 401s) and retry. `_public` calls
// (login/otp/reset — pre-auth) never refresh or hard-logout; their 401 surfaces
// so the UI can show "invalid credentials".
const makeErrorHandler = (instance) => async (error) => {
  const status = error.response?.status;
  const original = error.config ?? {};

  if (status === 401 && !original._public && !original._skipRefresh && !original._retry) {
    original._retry = true;
    const newToken = await refreshAccessToken();
    if (newToken) {
      original.headers = { ...original.headers, Authorization: `Bearer ${newToken}` };
      return instance(original);
    }
    hardLogout();
    return Promise.reject(error);
  }

  if (status === 401 && !original._public) {
    hardLogout();
    return Promise.reject(error);
  }

  if (status !== 401) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error?.message ||
      'Something went wrong';
    Utils.showMessage('error', message);
  }
  return Promise.reject(error);
};

apiAxios.interceptors.request.use(attachBearer, (error) => Promise.reject(error));
apiAxios.interceptors.response.use((r) => r, makeErrorHandler(apiAxios));

authAxios.interceptors.request.use(attachBearer, (error) => Promise.reject(error));
authAxios.interceptors.response.use((r) => r, makeErrorHandler(authAxios));

/**
 * Fire a request that must survive the page unloading.
 *
 * `beforeunload` is not a normal moment: axios (and any promise-based request)
 * is cancelled the instant the document goes away, so a PATCH sent from there
 * usually never leaves. `keepalive` is the platform's answer — the browser owns
 * the request and completes it after the page is gone.
 *
 * Not `navigator.sendBeacon`, which cannot set headers and so cannot carry the
 * bearer token. Deliberately fire-and-forget: nothing can consume a response
 * that arrives after the document is unloaded, and a rejection here must never
 * block the unload.
 *
 * @param {string} endpoint path under the API base, e.g. `/api/v1/chat/call/x`
 * @param {object} body
 * @param {string} [method]
 */
export const sendKeepalive = (endpoint, body, method = 'PATCH') => {
  try {
    const token = readToken('auth_token');
    fetch(`${BASE_URL}${endpoint}`, {
      method,
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body ?? {}),
    }).catch(() => {});
  } catch {
    // No token, no fetch, or the URL is unset. There is nothing useful to do at
    // unload time and nothing may throw out of here.
  }
};

// ─── api callers ─────────────────────────────────────────────────────────────
export default class ApiCaller {
  static async Get(endpoint) {
    return apiAxios.get(`${endpoint}`);
  }

  static async Post(endpoint, body) {
    return apiAxios.post(`${endpoint}`, body);
  }

  static async Patch(endpoint, body) {
    return apiAxios.patch(`${endpoint}`, body);
  }

  // `config` allows a request body via { data: {...} } — DELETE /user/devices
  // identifies the device to unregister by its FCM token.
  static async Delete(endpoint, config = {}) {
    return apiAxios.delete(`${endpoint}`, config);
  }
}

// Calls the central auth server directly. `opts` may set { _public: true } for
// pre-auth endpoints (login/otp/reset) so a 401 surfaces instead of triggering
// a refresh/hard-logout.
export class AuthApiCaller {
  static async Get(endpoint, opts = {}) {
    return authAxios.get(`${endpoint}`, opts);
  }

  static async Post(endpoint, body, opts = {}) {
    return authAxios.post(`${endpoint}`, body, opts);
  }

  static async Put(endpoint, body, opts = {}) {
    return authAxios.put(`${endpoint}`, body, opts);
  }

  static async Delete(endpoint, opts = {}) {
    return authAxios.delete(`${endpoint}`, opts);
  }
}
